import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Heart } from "lucide-react";
import { stripDemoMarker } from "@/lib/demo";
import { formatDistanceLabel, type ResolvedLocation } from "@/lib/geo";
import type { Item, User } from "@shared/schema";
import { hasListingPhoto } from "@shared/schema";
import { getListingImages } from "@shared/clothing";
import { ListingPhotoPlaceholder } from "@/components/ListingPhotoPlaceholder";

interface FeaturedProductCardProps {
  item: Item & { seller?: User | null };
  buyerLocation?: ResolvedLocation | null;
  fallbackBuyerZip?: string | null;
  isSaved?: boolean;
  onToggleSave?: (itemId: string) => void;
}

export function FeaturedProductCard({
  item,
  buyerLocation,
  fallbackBuyerZip,
  isSaved,
  onToggleSave,
}: FeaturedProductCardProps) {
  const [location] = useLocation();
  const [heartPop, setHeartPop] = useState(false);

  const price = parseFloat(item.price);
  const groceryAvg = item.groceryAvg ? parseFloat(item.groceryAvg) : null;
  const savings = groceryAvg && groceryAvg > price ? groceryAvg - price : null;

  const sellerLat =
    (item.seller as any)?.latitude == null || (item.seller as any)?.latitude === ""
      ? null
      : Number((item.seller as any).latitude);
  const sellerLng =
    (item.seller as any)?.longitude == null || (item.seller as any)?.longitude === ""
      ? null
      : Number((item.seller as any).longitude);
  const sellerName = item.seller
    ? stripDemoMarker(
        item.seller.displayName ||
          `${item.seller.firstName ?? ""} ${item.seller.lastName ?? ""}`
      ) || "Local Producer"
    : "Local Producer";

  const sellerLoc =
    Number.isFinite(sellerLat) && Number.isFinite(sellerLng)
      ? {
          source: "producer_address" as const,
          label: sellerName,
          zipCode: item.seller?.zipCode ?? item.zipCode ?? null,
          point: { latitude: sellerLat as number, longitude: sellerLng as number },
        }
      : null;

  const distanceLabel = formatDistanceLabel({
    buyerLocation,
    producerLocation: sellerLoc,
    fallbackBuyerZip: fallbackBuyerZip ?? null,
    fallbackProducerZip: item.seller?.zipCode ?? item.zipCode ?? null,
  });

  const displayName = stripDemoMarker(item.name) || item.name;
  const itemHref = `/items/${item.id}?back=${encodeURIComponent(
    location + (typeof window !== "undefined" ? window.location.search : ""),
  )}`;

  const handleHeart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setHeartPop(true);
    setTimeout(() => setHeartPop(false), 250);
    onToggleSave?.(item.id);
  };

  return (
    <Link
      href={itemHref}
      className="group flex h-full flex-col overflow-hidden rounded-2xl bg-card shadow-[0_8px_20px_rgba(90,72,40,0.10)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(90,72,40,0.14)]"
      data-testid={`card-featured-${item.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted/55">
        {hasListingPhoto(item) ? (
          <img
            src={getListingImages(item)[0]}
            alt={displayName}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <ListingPhotoPlaceholder category={item.category} />
        )}
        {onToggleSave && (
          <button
            type="button"
            onClick={handleHeart}
            className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-background/85 shadow-sm backdrop-blur transition ${
              heartPop ? "scale-110" : ""
            }`}
            aria-label={isSaved ? "Remove from saved" : "Save"}
            data-testid={`button-save-featured-${item.id}`}
          >
            <Heart
              className={`h-4 w-4 transition ${
                isSaved ? "fill-primary text-primary" : "text-foreground/70"
              }`}
            />
          </button>
        )}
        {distanceLabel && (
          <div className="absolute bottom-2 left-2 rounded-full bg-background/85 px-2 py-0.5 text-[10px] font-medium text-foreground shadow-sm backdrop-blur">
            {distanceLabel}
          </div>
        )}
        {item.status === "published_pending_payments" && (
          <div className="absolute right-2 bottom-2 rounded-full bg-brand-secondary/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground shadow-sm">
            Coming soon
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h3 className="line-clamp-2 text-sm font-semibold leading-tight tracking-tight text-foreground">
          {displayName}
        </h3>
        <div className="mt-auto flex items-baseline gap-1.5 pt-1">
          <span className="text-lg font-bold text-primary">${price.toFixed(2)}</span>
          {groceryAvg && (
            <span className="text-xs text-muted-foreground line-through">
              ${groceryAvg.toFixed(2)}
            </span>
          )}
          {savings && (
            <span className="ml-auto rounded-full bg-brand-secondary/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-secondary">
              Save ${savings.toFixed(2)}
            </span>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">{sellerName}</p>
      </div>
    </Link>
  );
}
